import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/landing/layout/Header";
import type {
  TncBlock,
  TncBody,
  TncHero,
  TncPreface,
  TncSection,
} from "./tncTypes";

// design tokens from the TnC design
const INK = "tw-text-[#0B0D10]";
const MUTED = "tw-text-[#5B6470]";
const SUBTLE = "tw-text-[#8A929C]";
const BORDER = "tw-border-[#E6E8EB]";
const BODY = `tw-text-base tw-leading-[27px] ${INK}`;

const pad = (n: number) => String(n).padStart(2, "0");

// renders **…** runs in bold
function Rich({ text }: { text: string }) {
  return (
    <>
      {text.split(/\*\*(.+?)\*\*/).map((part, i) =>
        i % 2 ? (
          <strong key={i} className="tw-font-semibold">
            {part}
          </strong>
        ) : (
          part
        )
      )}
    </>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="tw-space-y-2">
      {items.map((item) => (
        <li key={item} className="tw-flex">
          <span className={`tw-w-[55px] tw-shrink-0 tw-leading-[27px] ${SUBTLE}`}>
            •
          </span>
          <span className={BODY}>
            <Rich text={item} />
          </span>
        </li>
      ))}
    </ul>
  );
}

function Body({ items }: { items: TncBody[] }) {
  return (
    <div className="tw-space-y-[14px]">
      {items.map((item, i) =>
        typeof item === "string" ? (
          <p key={i} className={BODY}>
            <Rich text={item} />
          </p>
        ) : (
          <BulletList key={i} items={item.bullets} />
        )
      )}
    </div>
  );
}

function Blocks({ section, blocks }: { section: number; blocks: TncBlock[] }) {
  let clauseNo = 0;

  return (
    <div className="tw-space-y-[14px]">
      {blocks.map((block, i) => {
        switch (block.kind) {
          case "titled":
            clauseNo += 1;
            return (
              <div key={i} className="tw-flex tw-gap-4 tw-pt-3 first:tw-pt-0">
                <span
                  className={`tw-w-10 tw-shrink-0 tw-text-[13px] tw-leading-[27px] ${SUBTLE}`}
                >
                  {section}.{clauseNo}
                </span>
                <div className="tw-min-w-0 tw-flex-1">
                  <h3 className={`tw-text-[15px] tw-leading-[27px] tw-font-semibold ${INK}`}>
                    {block.title}
                  </h3>
                  <div className="tw-mt-2.5">
                    <Body items={block.body} />
                  </div>
                </div>
              </div>
            );

          case "para":
            return (
              <p key={i} className={`tw-pl-14 ${BODY}`}>
                <Rich text={block.text} />
              </p>
            );

          case "bullets":
            return (
              <div key={i} className="tw-pl-14">
                <BulletList items={block.items} />
              </div>
            );

          case "clause":
            clauseNo += 1;
            return (
              <div key={i} className="tw-flex tw-gap-4">
                <span
                  className={`tw-w-10 tw-shrink-0 tw-text-[13px] tw-leading-[27px] ${SUBTLE}`}
                >
                  {section}.{clauseNo}
                </span>
                <p className={`tw-text-base tw-leading-[27px] ${INK}`}>
                  {block.label && (
                    <span className="tw-font-semibold">{block.label} </span>
                  )}
                  {block.text}
                </p>
              </div>
            );

          case "note":
            return (
              <p
                key={i}
                className={`tw-pl-14 tw-text-base tw-leading-[27px] ${
                  block.strong ? `tw-font-semibold ${INK}` : MUTED
                }`}
              >
                {block.text}
              </p>
            );

          case "step":
            return (
              <h3
                key={i}
                className={`tw-pl-14 tw-pt-3 tw-text-[15px] tw-font-semibold ${INK}`}
              >
                {block.title}
              </h3>
            );

          case "dash":
            return (
              <div key={i} className="tw-flex tw-gap-4">
                <span
                  className={`tw-w-10 tw-shrink-0 tw-leading-[27px] ${SUBTLE}`}
                >
                  —
                </span>
                <p className={`tw-text-base tw-leading-[27px] ${INK}`}>
                  {block.label && (
                    <span className="tw-font-semibold">{block.label} </span>
                  )}
                  {block.text}
                </p>
              </div>
            );

          case "numbered":
            return (
              <ol key={i} className="tw-space-y-[14px]">
                {block.items.map((item, n) => (
                  <li key={item} className="tw-flex tw-gap-4">
                    <span
                      className={`tw-w-10 tw-shrink-0 tw-text-[13px] tw-leading-[27px] ${SUBTLE}`}
                    >
                      {n + 1}
                    </span>
                    <span className={BODY}>{item}</span>
                  </li>
                ))}
              </ol>
            );

          case "callout":
            return (
              <div key={i} className="tw-pl-14 tw-pt-2">
                <div className="tw-rounded-xl tw-bg-[#F5F7F9] tw-px-5 tw-py-4">
                  <p className={`tw-text-[13px] tw-font-semibold ${INK}`}>
                    {block.title}
                  </p>
                  <div className="tw-mt-1.5 tw-space-y-2">
                    {block.paragraphs.map((text) => (
                      <p key={text} className={`tw-text-[15px] tw-leading-6 ${MUTED}`}>
                        {text}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            );

          case "table":
            return (
              <div key={i} className="tw-pl-14 tw-pt-3">
                <Table head={block.head} rows={block.rows} mutedHead />
              </div>
            );
        }
      })}
    </div>
  );
}

function Table({
  head,
  rows,
  mutedHead = false,
}: {
  head?: [string, string];
  rows: [string, string][];
  mutedHead?: boolean;
}) {
  return (
    <table className="tw-w-full tw-text-left tw-text-[15px]">
      {head && (
        <thead>
          <tr className={`tw-border-b ${BORDER}`}>
            <th
              className={`tw-w-[264px] tw-pb-4 tw-font-semibold ${
                mutedHead ? MUTED : INK
              }`}
            >
              {head[0]}
            </th>
            <th className={`tw-pb-4 tw-font-semibold ${INK}`}>{head[1]}</th>
          </tr>
        </thead>
      )}
      <tbody>
        {rows.map(([left, right]) => (
          <tr key={left} className={`tw-border-b ${BORDER}`}>
            <td className={`tw-w-[264px] tw-py-4 ${MUTED}`}>{left}</td>
            <td className={`tw-py-4 ${INK}`}>{right}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface TncPageProps {
  hero: TncHero;
  preface?: TncPreface;
  sections: TncSection[];
  footerNote: string;
  // route of this TnC page, for the footer link
  path: string;
}

export default function TncPage({
  hero,
  preface,
  sections,
  footerNote,
  path,
}: TncPageProps) {
  const [activeId, setActiveId] = useState(sections[0].id);

  // highlight the contents entry for the section currently in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: "-120px 0px -70% 0px" }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  return (
    <div className="tw-bg-white">
      <Header />

      {/* hero */}
      <section className="tw-bg-[#F4F6F9] tw-pt-24">
        <div className="tw-max-w-[1120px] tw-mx-auto tw-px-6 xl:tw-px-0 tw-pt-14 tw-pb-14">
          <p className={`tw-text-[13px] ${SUBTLE}`}>
            {hero.breadcrumb}
            <span className="tw-mx-2">›</span>
            {hero.current}
          </p>

          <h1
            className={`tw-mt-3 tw-text-4xl md:tw-text-[52px] tw-leading-tight tw-font-bold tw-tracking-tight ${INK}`}
          >
            {hero.title}
          </h1>

          <div className={`tw-mt-6 tw-text-[17px] tw-leading-7 ${MUTED}`}>
            {hero.lines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        </div>
      </section>

      {/* body */}
      <div className="tw-max-w-[1120px] tw-mx-auto tw-px-6 xl:tw-px-0 tw-pt-14 tw-pb-24 lg:tw-grid lg:tw-grid-cols-[340px_1fr]">
        {/* contents */}
        <aside className="tw-hidden lg:tw-block">
          <nav className="tw-sticky tw-top-28 tw-w-[240px]">
            <p className={`tw-text-xs tw-font-semibold tw-tracking-wide ${SUBTLE}`}>
              Contents
            </p>

            <ul className="tw-mt-4">
              {sections.map(({ id, title }, i) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className={`tw-flex tw-gap-2 tw-px-3 tw-py-[10px] tw-text-[13px] tw-leading-[18px] tw-no-underline ${
                      activeId === id ? `tw-font-medium ${INK}` : MUTED
                    }`}
                  >
                    <span className={`tw-w-4 tw-shrink-0 ${SUBTLE}`}>
                      {pad(i + 1)}
                    </span>
                    {title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* sections */}
        <main className="tw-min-w-0 tw-space-y-16">
          {preface && (
            <section>
              <h2
                className={`tw-pb-4 tw-mb-6 tw-border-b ${BORDER} tw-text-[26px] tw-font-bold tw-tracking-tight ${INK}`}
              >
                {preface.title}
              </h2>
              <div className="tw-space-y-[14px]">
                {preface.paragraphs.map((text) => (
                  <p key={text} className={BODY}>
                    {text}
                  </p>
                ))}
              </div>
            </section>
          )}

          {sections.map((section, i) => (
            <section key={section.id} id={section.id} className="tw-scroll-mt-28">
              <div
                className={`tw-flex tw-items-center tw-gap-3.5 tw-pb-4 tw-mb-6 tw-border-b ${BORDER}`}
              >
                <span
                  className={`tw-inline-flex tw-items-center tw-justify-center tw-h-[25px] tw-min-w-[38px] tw-px-2 tw-rounded-md tw-bg-[#F1F3F5] tw-text-[13px] tw-font-semibold ${INK}`}
                >
                  {pad(i + 1)}
                </span>
                <h2 className={`tw-text-[26px] tw-font-bold tw-tracking-tight ${INK}`}>
                  {section.title}
                </h2>
              </div>

              {section.kind === "clauses" && (
                <Blocks section={i + 1} blocks={section.blocks} />
              )}

              {section.kind === "faq" && (
                <div className="tw-space-y-3">
                  {section.items.map(({ q, a }) => (
                    <div
                      key={q}
                      className={`tw-rounded-xl tw-border ${BORDER} tw-px-6 tw-py-5`}
                    >
                      <h3 className={`tw-text-[17px] tw-font-semibold ${INK}`}>
                        {q}
                      </h3>
                      <p className={`tw-mt-2 tw-text-[15px] tw-leading-6 ${MUTED}`}>
                        {a}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {section.kind === "sla" && (
                <>
                  <Table head={section.head} rows={section.rows} />
                  <p className={`tw-mt-4 tw-text-base tw-leading-[27px] ${MUTED}`}>
                    {section.note}
                  </p>
                </>
              )}

              {section.kind === "text" && (
                <p className={`tw-text-base tw-leading-[27px] ${MUTED}`}>
                  {section.text}
                </p>
              )}

              {section.kind === "contact" && section.intro && (
                <p className={`tw-pl-14 tw-mb-4 ${BODY}`}>{section.intro}</p>
              )}

              {section.kind === "contact" && (
                // cards line up with the intro text when there is one
                <div
                  className={`tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3.5 ${
                    section.intro ? "tw-pl-14" : ""
                  }`}
                >
                  {section.items.map(({ label, value, href }) => (
                    <a
                      key={label}
                      href={href}
                      target={href.startsWith("http") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      className={`tw-block tw-min-w-0 tw-rounded-xl tw-border ${BORDER} tw-px-4 tw-py-4 tw-no-underline hover:tw-border-[#C9CDD3] tw-transition-colors`}
                    >
                      <span className={`tw-block tw-text-[11px] tw-font-medium ${SUBTLE}`}>
                        {label}
                      </span>
                      <span
                        className={`tw-block tw-mt-1 tw-text-[15px] tw-font-semibold tw-tracking-tight tw-break-words ${INK}`}
                      >
                        {value}
                      </span>
                    </a>
                  ))}
                </div>
              )}

              {section.kind === "contact" && section.after && (
                <div className="tw-mt-6">
                  <Blocks section={i + 1} blocks={section.after} />
                </div>
              )}
            </section>
          ))}
        </main>
      </div>

      {/* footer */}
      <footer className={`tw-border-t ${BORDER}`}>
        <div className="tw-max-w-[1120px] tw-mx-auto tw-px-6 xl:tw-px-0 tw-py-10 tw-flex tw-flex-col md:tw-flex-row md:tw-justify-between tw-gap-6">
          <p className={`tw-max-w-[640px] tw-text-[13px] tw-leading-5 ${SUBTLE}`}>
            {footerNote}
          </p>

          <div className="tw-flex tw-items-start tw-gap-6 tw-text-[13px] tw-shrink-0">
            <Link to={path} className={`tw-no-underline ${INK}`}>
              Terms &amp; Conditions
            </Link>
            <Link to="/privacy-policy" className={`tw-no-underline ${INK}`}>
              Privacy policy
            </Link>
            <span className={SUBTLE}>© 2026 Protega</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
